"use client";

import { Add, Trash } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { useCallback } from "react";
import {
  BlurImage,
  CartQuantityControl,
  ProductPrice,
} from "@/components/core";
import { Button } from "@/components/ui";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { useAuth } from "@/context/auth";
import {
  checkVendorNotVerified,
  useAddToCart,
  useLoginRequired,
  useRemoveFromCart,
} from "@/hooks";
import { Link } from "@/i18n/navigation";
import { $api } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import type { components } from "@/types/api/api-types";

type ProductResponseDto = components["schemas"]["ProductResponseDto"];

export interface ShopItemCardProps extends ProductResponseDto {
  className?: string;
}

export const ShopItemCard = ({
  id,
  title,
  product_images,
  price,
  is_service,
  quantity: productQuantity,
  className,
  vendor,
}: ShopItemCardProps) => {
  const t = useTranslations("shop");
  const tCart = useTranslations("cart");
  const { isLoggedIn } = useAuth();
  const handleLoginRequired = useLoginRequired();

  // Query cart status for both products and services
  const cartItem = $api.useQuery(
    "get",
    "/cart/product/{productId}/quantity",
    { params: { path: { productId: id } } },
    { enabled: isLoggedIn },
  );

  const removeFromCart = useRemoveFromCart({
    onSuccess: () => {
      cartItem.refetch();
    },
  });

  // For quantity increment (no toast) - only used for products
  const incrementCart = useAddToCart({
    onSuccess: () => {
      cartItem.refetch();
    },
  });

  // For first add (with toast)
  const addToCartFirst = useAddToCart({
    showAddedToast: true,
    isService: is_service,
    onSuccess: () => {
      cartItem.refetch();
    },
  });

  const handleFirstAdd = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleLoginRequired(() => {
        if (
          checkVendorNotVerified(
            vendor?.is_verified,
            tCart("toasts.vendorNotVerified"),
          )
        )
          return;
        addToCartFirst.mutate({ body: { product_id: id } });
      })();
    },
    [handleLoginRequired, addToCartFirst, id, vendor?.is_verified, tCart],
  );

  const handleIncrement = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleLoginRequired(() => {
        if (
          checkVendorNotVerified(
            vendor?.is_verified,
            tCart("toasts.vendorNotVerified"),
          )
        )
          return;
        incrementCart.mutate({ body: { product_id: id } });
      })();
    },
    [handleLoginRequired, incrementCart, id, vendor?.is_verified, tCart],
  );

  const handleDecrement = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      removeFromCart.mutate({
        params: {
          path: { id: cartItem.data?.data.id as string },
        },
      });
    },
    [removeFromCart, cartItem.data?.data?.id],
  );

  const handleRemoveService = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      removeFromCart.mutate({
        params: {
          path: { id: cartItem.data?.data.id as string },
        },
      });
    },
    [removeFromCart, cartItem.data?.data?.id],
  );

  // Check if product is unavailable (only for products, not services)
  const isUnavailable = !is_service && (productQuantity ?? 0) <= 0;

  return (
    <Item
      className={cn(
        "flex items-end gap-5 border-s-0 border-e-0 rounded-none border-t border-b border-stroke-subtle py-5 px-4",
        className,
      )}
    >
      <Link href={`/shops/items/${id}`}>
        <ItemMedia className="size-[140px] rounded-sm overflow-hidden">
          {product_images?.[0]?.url ? (
            <BlurImage
              src={product_images[0].url}
              alt={title}
              width={140}
              height={140}
              className="aspect-square object-cover w-full h-full"
              loading="lazy"
            />
          ) : (
            <BlurImage
              src={"/images/placeholders/item-placeholder.webp"}
              alt={title}
              width={140}
              height={140}
              className="aspect-square object-cover w-full h-full"
              loading="lazy"
            />
          )}
        </ItemMedia>
      </Link>
      <ItemContent className="space-y-4">
        <Link href={`/shops/items/${id}`}>
          <div>
            <ItemTitle className="text-sm w-full text-end">
              {isUnavailable ? (
                <span className="text-text-disabled font-semibold text-sm text-start block w-full">
                  {t("card.unavailable")}
                </span>
              ) : (
                <ProductPrice
                  className="w-full"
                  seller_share_unit={price?.seller_share_unit}
                  seller_share_currency={price?.seller_share_currency}
                  seller_settlement_currency={
                    (price as { seller_settlement_currency?: "UVM" | "USDT" })
                      ?.seller_settlement_currency
                  }
                  bv={price?.bv}
                />
              )}
            </ItemTitle>
            <ItemDescription className="line-clamp-2 text-text-secondary font-bold text-xs">
              {title}
            </ItemDescription>
          </div>
        </Link>
        {/* For products: show quantity controls if in cart */}
        {/* For services: show Remove from Cart button if in cart */}
        {!is_service && isLoggedIn && cartItem.data?.data?.quantity ? (
          <CartQuantityControl
            size="sm"
            quantity={cartItem.data.data.quantity}
            max={productQuantity}
            incrementLoading={incrementCart.isPending}
            onIncrement={handleIncrement}
            decrementLoading={removeFromCart.isPending}
            onDecrement={handleDecrement}
            className="border-stroke-light w-fit ms-auto"
          />
        ) : is_service && isLoggedIn && cartItem.data?.data?.id ? (
          <Button
            variant="bordered"
            colors="secondary"
            startIcon={<Trash size={18} />}
            className="w-full"
            onClick={handleRemoveService}
            loading={removeFromCart.isPending}
          >
            {t("card.removeFromCart")}
          </Button>
        ) : (
          <Button
            colors={"secondary"}
            startIcon={<Add />}
            className={cn("w-full", isUnavailable && "invisible")}
            onClick={handleFirstAdd}
            loading={addToCartFirst.isPending}
          >
            {t("card.addToCart")}
          </Button>
        )}
      </ItemContent>
    </Item>
  );
};
